import React, { ReactNode } from 'react';
import { Box, CSSObject, Palette, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { convertStylesToStyled } from '@/_utils/utils';

type FieldsetProps = {
  label: string;
  color?: keyof Palette;
  children: ReactNode;
  style?: CSSObject;
};

const Fieldset = ({ label, color = 'default', children, style }: FieldsetProps) => (
  <StyledBox color={color} extra={style}>
    <Label variant="h4" color={color}>{label}</Label>
    {children}
  </StyledBox>
);

export default Fieldset;

type StyledBoxProps = {
  color: keyof Palette;
  extra?: CSSObject;
};
const StyledBox = styled(Box)<StyledBoxProps>`
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
  border-radius: ${({ theme }) => theme.shape.borderRadius};
  border-width: 2px;
  padding: ${({ theme }) => theme.spacing(3)};
  border-style: solid;
  position: relative;
  margin: 0;
  ${({ theme }) => convertStylesToStyled(theme?.components?.MuiOutlinedInput?.styleOverrides?.notchedOutline)}
  ${({ extra }) => convertStylesToStyled(extra)}

  & .MuiFormControlLabel-root {
    margin-right: ${({ theme }) => theme.spacing(5)};
  }

  border-color: ${({ theme, color }) => {
    switch (color) {
      case 'primary':
        return theme.palette.primary.main + '99';
      case 'secondary':
        return theme.palette.secondary.main + '99';
      case 'tertiary':
        return theme.palette.tertiary?.main + '99';
      case 'success':
        return theme.palette.success.main + '99';
      case 'teal':
        return `var(--teal-main)`;
      case 'default':
      default:
        return theme.palette.default.main;
    }
  }};

  & strong, & .Mui-checked, & svg {
    color: ${({ theme, color }) => {
      switch (color) {
        case 'primary':
          return theme.palette.primary.main;
        case 'secondary':
          return theme.palette.secondary.main;
        case 'tertiary':
          return theme.palette.tertiary?.main;
        case 'success':
          return theme.palette.success.main;
        case 'teal':
          return `var(--teal-main)`;
        case 'default':
        default:
          return theme.palette.default.main;
      }
    }}
  }

  & mark {
    background: ${({ theme, color }) => {
      switch (color) {
        case 'primary':
          return theme.palette.primary.main + '99';
        case 'secondary':
          return theme.palette.secondary.main + '99';
        case 'tertiary':
          return theme.palette.tertiary?.main + '99';
        case 'success':
          return theme.palette.success.main + '99';
        case 'teal':
          return `var(--teal-main)`;
        case 'default':
        default:
          return theme.palette.default.main;
      }
    }}
    border-color: ${({ theme, color }) => {
      switch (color) {
        case 'secondary':
          return theme.palette.secondary.main + '5a';
        case 'tertiary':
          return theme.palette.tertiary?.main + '5a';
        case 'teal':
          return `var(--teal-light)`;
        case 'default':
          return theme.palette.default.main;
        default:
          return 'inherit';
      }
    }}
  }
`;

const Label = styled(Typography)`
  font-size: 1.1rem;
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  align-items: center;
  display: inline-flex;

  & svg {
    margin-right: ${({ theme }) => theme.spacing(1)};
    background: ${({ theme }) => theme.palette.background.paper};
  }
`;
